import type { ResourceId } from './ids';

/**
 * The system exchange (GDD §6). Prices are quoted in ГС (galactic standard),
 * an accounting unit; faction currencies convert to it (issue #3).
 */
export const TRADE_GOOD_IDS = [
  'metals',
  'helium3',
  'food',
  'alloys',
  'electronics',
  'exotics',
] as const satisfies readonly ResourceId[];
export type TradeGoodId = (typeof TRADE_GOOD_IDS)[number];

export interface TradeGoodDef {
  id: TradeGoodId;
  /** Price the market drifts back to, in ГС per unit. */
  basePrice: number;
  /**
   * Price impact per unit traded: each unit bought multiplies the price by
   * (1 + impact), each unit sold divides it. Rare goods react more sharply.
   */
  impact: number;
}

export const TRADE_GOODS: Readonly<Record<TradeGoodId, TradeGoodDef>> = {
  metals: { id: 'metals', basePrice: 10, impact: 0.002 },
  helium3: { id: 'helium3', basePrice: 20, impact: 0.003 },
  food: { id: 'food', basePrice: 12, impact: 0.002 },
  alloys: { id: 'alloys', basePrice: 25, impact: 0.003 },
  electronics: { id: 'electronics', basePrice: 40, impact: 0.004 },
  exotics: { id: 'exotics', basePrice: 80, impact: 0.01 },
};

export const MARKET = {
  /** Exchange fee on every trade (GDD §6: 10%). Removed from the economy. */
  fee: 0.1,
  /** Largest single order, in units. Bigger deals go through contracts. */
  maxOrderUnits: 200,
  /**
   * Prices return to base exponentially; this is the half-life of the gap in
   * log space, in ticks (3 economic cycles = 90 s).
   */
  reversionHalfLifeTicks: 900,
  /** Hard bounds relative to base, so no sequence of trades breaks the market. */
  minPriceFactor: 0.2,
  maxPriceFactor: 5,
} as const;

/**
 * «Торговые алгоритмы» (#123, the author's idea of 23.10.2026): per good, a
 * rule run once a cycle on the exchange at its prices and fees.
 *  - sell: what is above `keep`, only while the good is made regularly (the
 *    last cycle's net output is positive) and the price is not in a slump;
 *  - buy: up to `keep`, while the price is not inflated.
 */
export const AUTO_TRADE = {
  /**
   * Stock kept on top of what the next cycle takes (#143, the author's call of
   * 27.10.2026: a rule works from the planet's needs, not from a number).
   */
  defaultKeep: 0,
  /** The most a rule from an older match or a script may keep on top. */
  maxKeep: 500,
  /** Never sell below this share of the base price... */
  minSellRatio: 0.8,
  /** ...nor buy above this one. */
  maxBuyRatio: 1.5,
} as const;
